/**
 * 채팅 인터페이스 컴포넌트
 * 카카오톡 스타일의 대화형 채팅 UI
 */

import React, { useEffect, useRef } from 'react';
import { useChatStore } from '../../stores/chatStore';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { BotAvatar } from './BotAvatar';

/**
 * ChatInterface 컴포넌트
 *
 * - 메시지 목록 표시
 * - 자동 스크롤 (새 메시지 시 하단으로)
 * - 타이핑 인디케이터
 * - 레트로 게임 스타일 채팅창
 */
export const ChatInterface: React.FC = () => {
  const { messages, isBotTyping, currentBotEmotion } = useChatStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  /**
   * 채팅창 내부 자동 스크롤
   * scrollTop을 직접 조작하여 채팅창 내부만 스크롤 (페이지 전체는 영향 없음)
   * 새 메시지가 추가될 때마다 하단으로 스크롤되어 새 메시지가 보임
   */
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      // 채팅 컨테이너의 scrollTop을 최대값으로 설정 (하단으로 스크롤)
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isBotTyping]);

  return (
    <div
      className="flex flex-col bg-black
                 border-4 border-[#4b5563]
                 shadow-[0_4px_0_#374151,0_6px_12px_rgba(0,0,0,0.5)]
                 rounded-lg overflow-hidden h-full"
    >
      {/* 채팅 메시지 영역 */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-2
                   bg-gradient-to-b from-gray-900 to-black"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#4b5563 #1f2937',
        }}
      >
        {/* 메시지가 없을 때 */}
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <BotAvatar emotion="happy" size="large" />
            <p className="font-pixel text-sm text-gray-500 mt-4">
              채팅을 시작해보세요!
            </p>
          </div>
        )}

        {/* 메시지 목록 */}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* 타이핑 인디케이터 */}
        {isBotTyping && (
          <div className="flex items-start gap-2 mb-4">
            <BotAvatar emotion={currentBotEmotion} size="medium" />
            <div
              className="px-2 py-1 bg-[#9bbc0f] rounded-tr-2xl rounded-br-2xl rounded-bl-2xl
                         border-2 border-[#0f380f]
                         shadow-[3px_3px_0px_0px_rgba(0,0,0,0.3)]"
            >
              <TypingIndicator />
            </div>
          </div>
        )}

        {/* 스크롤 타겟 */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
